/**
 * Utility for safely converting JSON objects to clean HTML format
 * with proper hierarchy, clickable links, and security measures.
 */

// URL regex pattern for detecting links
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

// Function to create a fresh regex instance for testing
const createUrlRegex = () => /(https?:\/\/[^\s]+)/g;

// Function to safely escape HTML content
const escapeHtml = (text: string): string => {
  if (typeof document !== 'undefined') {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  // Fallback for Node.js environment (testing)
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Function to detect and convert URLs to clickable links
const convertUrlsToLinks = (text: string): string => {
  return text.replace(URL_REGEX, (url) => {
    const escapedUrl = escapeHtml(url);
    return `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline">${escapedUrl}</a>`;
  });
};

// Function to format a value for display
const formatValue = (value: any, depth: number = 0): string => {
  if (value === null) {
    return '<span class="text-gray-500 dark:text-gray-400 italic">null</span>';
  }

  if (value === undefined) {
    return '<span class="text-gray-500 dark:text-gray-400 italic">undefined</span>';
  }

  if (typeof value === 'string') {
    const escapedValue = escapeHtml(value);
    const withLinks = convertUrlsToLinks(escapedValue);
    return `<span class="text-green-700 dark:text-green-400">"${withLinks}"</span>`;
  }

  if (typeof value === 'number') {
    return `<span class="text-blue-600 dark:text-blue-400">${value}</span>`;
  }

  if (typeof value === 'boolean') {
    return `<span class="text-purple-600 dark:text-purple-400">${value}</span>`;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '<span class="text-gray-500 dark:text-gray-400 italic">[]</span>';
    }

    const items = value.map((item, index) => {
      const formattedItem = formatValue(item, depth + 1);
      return `<div class="ml-4 border-l-2 border-gray-200 dark:border-gray-600 pl-3">
        <span class="text-gray-500 dark:text-gray-400">${index}:</span> ${formattedItem}
      </div>`;
    }).join('');

    return `<div class="space-y-1">${items}</div>`;
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return '<span class="text-gray-500 dark:text-gray-400 italic">{}</span>';
    }

    const items = keys.map(key => {
      const formattedValue = formatValue(value[key], depth + 1);
      return `<div class="ml-4 border-l-2 border-gray-200 dark:border-gray-600 pl-3">
        <span class="font-medium text-gray-900 dark:text-gray-100">${escapeHtml(key)}:</span> ${formattedValue}
      </div>`;
    }).join('');

    return `<div class="space-y-1">${items}</div>`;
  }

  // Fallback for other types
  return `<span class="text-gray-600 dark:text-gray-300">${escapeHtml(String(value))}</span>`;
};

// Function to create a hierarchical HTML structure
const createHierarchicalHtml = (obj: any, title?: string): string => {
  if (obj === null || obj === undefined) {
    return `<div class="text-gray-500 dark:text-gray-400 italic">No data available</div>`;
  }

  if (typeof obj === 'string') {
    const escapedValue = escapeHtml(obj);
    const withLinks = convertUrlsToLinks(escapedValue);
    return `<div class="whitespace-pre-wrap text-gray-900 dark:text-gray-100">${withLinks}</div>`;
  }

  if (typeof obj === 'number' || typeof obj === 'boolean') {
    return `<div>${formatValue(obj)}</div>`;
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return `<div class="text-gray-500 dark:text-gray-400 italic">Empty array</div>`;
    }

    const items = obj.map((item, index) => {
      const formattedItem = formatValue(item, 1);
      return `<div class="border-l-2 border-blue-200 dark:border-blue-700 pl-4 py-1">
        <div class="flex items-start">
          <span class="text-gray-500 dark:text-gray-400 font-mono text-sm mr-2">[${index}]:</span>
          <div class="flex-1">${formattedItem}</div>
        </div>
      </div>`;
    }).join('');

    return `<div class="space-y-2">${items}</div>`;
  }

  if (typeof obj === 'object') {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return `<div class="text-gray-500 dark:text-gray-400 italic">Empty object</div>`;
    }

    const sections = keys.map(key => {
      const value = obj[key];
      const formattedValue = formatValue(value, 1);

      return `<div class="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div class="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">${escapeHtml(key)}</h3>
        </div>
        <div class="p-4">
          ${formattedValue}
        </div>
      </div>`;
    }).join('');

    return `<div class="space-y-4">${sections}</div>`;
  }

  return `<div class="text-gray-600 dark:text-gray-300">${escapeHtml(String(obj))}</div>`;
};

export const isStringArray = (data: any): data is string[] => {
  return Array.isArray(data) && data.length > 0 && data.every(item => typeof item === 'string');
};

// Main function to convert JSON to HTML
export const jsonToHtml = (data: any, title?: string): string => {
  try {
    // If data is a string, try to parse it as JSON
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch {
        // If it's not valid JSON, treat it as a plain string
        const escapedValue = escapeHtml(data);
        const withLinks = convertUrlsToLinks(escapedValue);
        return `<div class="whitespace-pre-wrap">${withLinks}</div>`;
      }
    }
    return createHierarchicalHtml(parsedData, title);
  } catch (error) {
    console.error('Error converting JSON to HTML:', error);
    return `<div class="text-red-600 dark:text-red-400">Error formatting data: ${escapeHtml(String(error))}</div>`;
  }
};

// Function to check if data contains URLs
export const containsUrls = (data: any): boolean => {
  const dataString = typeof data === 'string' ? data : JSON.stringify(data);
  return createUrlRegex().test(dataString);
};

// Function to get a preview of the data structure
export const getDataPreview = (data: any, maxDepth: number = 2): string => {
  const getPreview = (obj: any, depth: number = 0): string => {
    if (depth >= maxDepth) {
      return '...';
    }
    
    if (obj === null) {
      return 'null';
    }
    
    if (obj === undefined) {
      return 'undefined';
    }
    
    if (typeof obj === 'string') {
      return obj.length > 50 ? obj.substring(0, 50) + '...' : obj;
    }
    
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return String(obj);
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]';
      return `[${obj.length} items]`;
    }
    
    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) return '{}';
      return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '...' : ''}}`;
    }
    
    return String(obj);
  };
  
  return getPreview(data);
}; 